import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { claus, obteActivacio } from '@/api/activacions';
import type { MissatgeTempsReal, TipusActivacio, TipusArea } from '@/api/types';
import { Icona, type NomIcona } from '@/components/Icona';
import { useMe } from '@/permisos/MeContext';
import { useTopic } from '@/realtime/RealtimeContext';
import { esVeientXat, rutaXat } from '@/pages/operacio/suport';

export interface PayloadActivacioNova {
  activacioId: number;
  codi?: string | null;
  titol?: string | null;
  tipus?: TipusActivacio | null;
  critica?: boolean;
  areaTipus?: TipusArea | null;
  servei?: string | null;
  destinatariId?: number | null;
  nivell?: number | null;
}

export interface PayloadMencio {
  activacioId: number;
  codi?: string | null;
  autor?: string | null;
  text?: string | null;
}

/** Nou missatge al xat d'una activació de la qual l'usuari és membre. */
export interface PayloadMissatgeActivacio {
  activacioId: number;
  /** Imatge (acció NOTA). */
  accioId?: number | null;
  /** Text de la conversa. */
  missatgeId?: number | null;
  autorId?: number | null;
}

type TipusAvis = 'activacio.nova' | 'mencio' | 'activacio.missatge';

interface Avis {
  clau: number;
  activacioId: number;
  critic: boolean;
  tipus: TipusAvis;
  codi?: string | null;
  titol?: string | null;
  text?: string | null;
}

const DURADA_MS = 12_000;
const DURADA_MISSATGE_MS = 6_000;

const ICONA: Record<TipusAvis, NomIcona> = {
  'activacio.nova': 'bell',
  mencio: 'at',
  'activacio.missatge': 'message',
};

/**
 * Avisos emergents del canal personal `usuari:{id}`: noves activacions on l'usuari és
 * destinatari (vermelles si són crítiques), mencions i missatges nous al xat d'una activació
 * (aquests només si no s'està veient la pestanya Xat d'aquella activació i no s'ha silenciat).
 * Els avisos de missatges i mencions enllacen al detall obert a la pestanya Xat.
 */
export function AvisosTempsReal() {
  const { t } = useTranslation('comu');
  const { me } = useMe();
  const qc = useQueryClient();
  const { pathname, search } = useLocation();
  const ruta = useRef({ pathname, search });
  const [avisos, setAvisos] = useState<Avis[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const accionsVistes = useRef(new Set<string>());

  useEffect(() => {
    ruta.current = { pathname, search };
  }, [pathname, search]);

  const veientXat = (id: number) => esVeientXat(ruta.current.pathname, ruta.current.search, id);

  const treu = useCallback((clau: number) => {
    setAvisos((a) => a.filter((x) => x.clau !== clau));
    const tm = timers.current.get(clau);
    if (tm) clearTimeout(tm);
    timers.current.delete(clau);
  }, []);

  useEffect(() => {
    const actius = timers.current;
    return () => actius.forEach((tm) => clearTimeout(tm));
  }, []);

  const mostra = useCallback(
    (avis: Omit<Avis, 'clau'>, durada: number | null) => {
      seq.current += 1;
      const clau = seq.current;
      setAvisos((a) => [{ ...avis, clau }, ...a].slice(0, 4));
      if (durada != null)
        timers.current.set(
          clau,
          setTimeout(() => treu(clau), durada),
        );
    },
    [treu],
  );

  useTopic(me ? `usuari:${me.id}` : null, (m: MissatgeTempsReal) => {
    if (m.type === 'activacio.missatge') {
      const p = (m.payload ?? {}) as Partial<PayloadMissatgeActivacio>;
      const id = p.activacioId;
      if (id == null) return;
      void qc.invalidateQueries({ queryKey: claus.part(id, 'missatges') });
      void qc.invalidateQueries({ queryKey: claus.part(id, 'accions') });
      // L'esdeveniment també arriba a l'autor (per refrescar les altres pestanyes), però no se li avisa.
      if (p.autorId != null && p.autorId === me?.id) return;
      const clau =
        p.accioId != null ? `a${p.accioId}` : p.missatgeId != null ? `m${p.missatgeId}` : null;
      if (clau) {
        if (accionsVistes.current.has(clau)) return;
        accionsVistes.current.add(clau);
      }
      if (veientXat(id)) return;
      // El detall (de la memòria cau si és recent) diu el codi i si l'usuari l'ha silenciat.
      void qc
        .fetchQuery({
          queryKey: claus.activacio(id),
          queryFn: () => obteActivacio(id),
          staleTime: 30_000,
        })
        .then(
          (a) => a,
          () => undefined,
        )
        .then((a) => {
          if (a?.silenciada || veientXat(id)) return;
          mostra(
            {
              activacioId: id,
              tipus: m.type as TipusAvis,
              critic: false,
              codi: a?.codi,
              titol: a?.titol,
            },
            DURADA_MISSATGE_MS,
          );
        });
      return;
    }
    if (m.type !== 'activacio.nova' && m.type !== 'mencio') return;
    const p = (m.payload ?? {}) as Partial<PayloadActivacioNova & PayloadMencio>;
    if (p.activacioId == null) return;
    void qc.invalidateQueries({ queryKey: ['activacions'] });
    void qc.invalidateQueries({ queryKey: ['activacio', p.activacioId] });
    const critic = m.type === 'activacio.nova' && Boolean(p.critica);
    mostra(
      {
        activacioId: p.activacioId,
        tipus: m.type,
        critic,
        codi: p.codi,
        titol: m.type === 'activacio.nova' ? p.titol : p.autor,
        text: m.type === 'activacio.nova' ? p.servei : p.text,
      },
      // Les crítiques es queden fins que l'usuari les tanca.
      critic ? null : DURADA_MS,
    );
  });

  if (!avisos.length) return null;
  return (
    <div className="toasts" aria-live="assertive">
      {avisos.map((a) => (
        <div
          key={a.clau}
          className={`toast${a.critic ? ' toast--critic' : ''}${a.tipus === 'activacio.missatge' ? ' toast--discret' : ''}`}
          role={a.tipus === 'activacio.missatge' ? 'status' : 'alert'}
          data-testid="avis-temps-real"
          data-tipus={a.tipus}
        >
          <div className="toast__ico">
            <Icona nom={a.critic ? 'exclamationmark-octagon' : ICONA[a.tipus]} />
          </div>
          <Link
            to={
              a.tipus === 'activacio.nova'
                ? `/activacions/${a.activacioId}`
                : rutaXat(a.activacioId)
            }
            className="truncate"
            style={{ flex: 1 }}
            onClick={() => treu(a.clau)}
          >
            {a.tipus === 'activacio.missatge' ? (
              <>
                <div className="toast__titol truncate">
                  {t('avisos.missatgeA', { codi: a.codi ?? `#${a.activacioId}` })}
                </div>
                {a.titol ? <div className="toast__text truncate">{a.titol}</div> : null}
              </>
            ) : (
              <>
                <div className="toast__app">
                  {t(
                    a.tipus === 'mencio'
                      ? 'avisos.mencio'
                      : a.critic
                        ? 'avisos.critica'
                        : 'avisos.nova',
                  )}
                  {a.codi ? ` · ${a.codi}` : ''}
                </div>
                {a.titol ? <div className="toast__titol truncate">{a.titol}</div> : null}
                {a.text ? <div className="toast__text truncate">{a.text}</div> : null}
              </>
            )}
          </Link>
          <button
            type="button"
            className="toast__tanca"
            aria-label={t('accions.tancar')}
            onClick={() => treu(a.clau)}
          >
            <Icona nom="xmark" />
          </button>
        </div>
      ))}
    </div>
  );
}
